/*
 * История обращения: та же лента, что в профиле клиента, но для человека,
 * у которого дела ещё нет. Таких в системе большинство — 107 из 109.
 *
 * 🔴 ЗДЕСЬ ЖИВУТ ТРИ ДЕЙСТВИЯ НАД ОБРАЩЕНИЕМ (quick-260911-27v, Д-04).
 *    До 11.09.2026 на этой карточке не было НИ ОДНОГО действия, а `Delete` и
 *    `Convert to Client` стояли в КАЖДОЙ строке списка: на ста строках — сто
 *    разрушительных кнопок под курсором, причём `Convert` без подтверждения
 *    вовсе. Возможности не потеряны — они переехали туда, где человек смотрит
 *    на ОДНО обращение и видит его целиком.
 *
 * 🔴 КАРТОЧКА ОБЩАЯ НА ВСЕХ АРЕНДАТОРОВ, и действия появляются у всех четверых
 *    (mlg · fitenko · eln · elfny). Это сказано вслух, а не умолчано: рубежа
 *    арендатора здесь нет и раньше не было. Захочет владелец гейтить — это
 *    правка в одну строку, и она его решение, а не наше.
 *
 * 🔴 КОРЗИНЫ УДАЛЁННЫХ ОБРАЩЕНИЙ В ИНТЕРФЕЙСЕ НЕТ. Ручки существуют —
 *    `GET /leads/deleted` (leads.py:83) и `POST /leads/{id}/restore`
 *    (leads.py:547), — но потребителей во фронте у них НОЛЬ (проверено грепом).
 *    Значит удаление обратимо в базе и НЕОБРАТИМО для человека. Это НАЗВАННАЯ
 *    граница, а не починка: экран восстановления — отдельная задача.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import HistoryTimeline from '../components/history/HistoryTimeline';
import apiService from '../services/apiService';
import { LeadEditModal } from './LeadsPage';
import { useUiLanguage } from '../context/UiLanguageContext';
import { formatLeadName } from '../utils/historyFormatters';

const LeadHistoryPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, lang } = useUiLanguage();
    const [history, setHistory] = useState(null);
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pravim, postavitPravim] = useState(false);
    const [zanyato, postavitZanyato] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        apiService.getLeadHistory(id)
            .then((data) => { if (!cancelled) setHistory(data); })
            .catch((e) => { if (!cancelled) setError(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id]);

    // Само обращение спрашивается ОТДЕЛЬНО: подтверждение разрушительного
    // действия обязано называть ПОСЛЕДСТВИЕ поимённо, а для этого нужен адрес
    // почты. Лента его не несёт.
    useEffect(() => {
        let cancelled = false;
        apiService.getLead(id)
            .then((d) => { if (!cancelled) setLead(d); })
            .catch(() => { if (!cancelled) setLead(null); });
        return () => { cancelled = true; };
    }, [id]);

    const fallbackTitle = t('practice.lead_history.title_fallback', { id });
    const rawName = (lead && lead.full_name) || (history && history.display_name) || fallbackTitle;
    const imya = formatLeadName(rawName, t, lang);

    const perevesti = useCallback(async () => {
        // 🔴 ЭТО ДЕЙСТВИЕ НЕ БЕЗОБИДНО: оно создаёт учётную запись с временным
        //    паролем и ОТПРАВЛЯЕТ ПИСЬМО (leads.py:287-386).
        const adres = (lead && lead.email) || t('practice.lead_history.unknown_email');
        const soglasie = window.confirm(t('practice.lead_history.convert_confirm_msg', { name: imya, email: adres }));
        if (!soglasie) return;
        postavitZanyato(true);
        try {
            const otvet = await apiService.convertLead(id);
            toast.success(otvet.message || t('practice.lead_history.convert_success'));
            setLead((b) => (b ? { ...b, status: 'CONVERTED', case_id: otvet.case_id } : b));
            if (otvet.case_id) {
                toast(t('practice.lead_history.convert_toast_opening', { id: otvet.case_id }), { icon: '📂' });
                setTimeout(() => navigate(`/cases/${otvet.case_id}`), 800);
            }
        } catch (e) {
            toast.error(t('practice.lead_history.convert_failed', { error: e.message }));
        } finally {
            postavitZanyato(false);
        }
    }, [id, imya, lead, navigate, t]);

    const udalit = useCallback(async () => {
        const soglasie = window.confirm(t('practice.lead_history.delete_confirm_msg', { name: imya }));
        if (!soglasie) return;
        postavitZanyato(true);
        try {
            await apiService.deleteLead(id);
            toast.success(t('practice.lead_history.delete_success', { name: imya }));
            navigate('/leads');
        } catch (e) {
            toast.error(t('practice.lead_history.delete_failed', { error: e.message }));
            postavitZanyato(false);
        }
    }, [id, imya, navigate, t]);

    const svyazannoeDeloId = (lead && lead.case_id) || (history && history.case_ids && history.case_ids[0]);

    return (
        <div className="page-container" style={{ padding: '24px' }}>
            {pravim && lead ? (
                <LeadEditModal
                    lead={lead}
                    onClose={() => postavitPravim(false)}
                    onSave={(obnovlennoe) => setLead(obnovlennoe)}
                />
            ) : null}

            <button
                className="btn-secondary"
                onClick={() => navigate(-1)}
                style={{ marginBottom: '16px' }}
            >
                {t('practice.lead_history.back')}
            </button>

            <h1 style={{ margin: '0 0 4px', fontSize: '22px' }} data-user-content="true">
                {formatLeadName(history?.display_name || lead?.full_name, t, lang) || fallbackTitle}
            </h1>
            <p style={{ margin: '0 0 8px', opacity: 0.6, fontSize: '13px' }}>
                {t('practice.lead_history.inquiry_num', { id })}
                {history?.case_ids?.length ? t('practice.lead_history.cases_count', { count: history.case_ids.length }) : t('practice.lead_history.no_cases')}
            </p>

            {/* ДЕЙСТВИЯ НАД ОДНИМ ОБРАЩЕНИЕМ */}
            {lead ? (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '0 0 16px', alignItems: 'center' }}>
                    <button type="button" className="btn btn-secondary btn-sm"
                            disabled={zanyato} onClick={() => postavitPravim(true)}>
                        {t('practice.lead_history.btn_edit')}
                    </button>
                    {String(lead.status).toLowerCase() !== 'converted' ? (
                        <button type="button" className="btn btn-primary btn-sm"
                                disabled={zanyato}
                                title={t('practice.lead_history.btn_convert_title')}
                                onClick={perevesti}>
                            {t('practice.lead_history.btn_convert')}
                        </button>
                    ) : (
                        svyazannoeDeloId && (
                            <button
                                type="button"
                                className="btn btn-sm"
                                style={{
                                    background: 'var(--primary)',
                                    color: 'var(--accent-soft)',
                                    border: '1px solid var(--accent)',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                                onClick={() => navigate(`/cases/${svyazannoeDeloId}`)}
                            >
                                {t('practice.lead_history.btn_open_case', { id: svyazannoeDeloId })}
                            </button>
                        )
                    )}
                    <button type="button" className="btn btn-danger btn-sm"
                            disabled={zanyato} onClick={udalit}>
                        {t('practice.lead_history.btn_delete')}
                    </button>
                </div>
            ) : null}

            <HistoryTimeline history={history} loading={loading} error={error} />
        </div>
    );
};

export default LeadHistoryPage;
